import { collection, addDoc, serverTimestamp, query, where, getDocs, writeBatch, getDoc, doc } from 'firebase/firestore';
import { db } from './firebaseConfig';

// Add this helper function at the top of notificationUtils.js or in a shared constants/utils file
const getFullReportName = (reportTypeShort) => {
  switch (reportTypeShort) {
    case 'DOR': return 'Daily Observation Report';
    case 'EOWR': return 'End of Week Report';
    case 'OSR': return 'Officer Safety Report';
    default: return reportTypeShort; // Fallback to the short type if unknown
  }
};

/**
 * Creates a notification for a specific user
 * @param {string} userId - The recipient's user ID
 * @param {string} message - The notification message
 * @param {string} title - Optional title for the notification
 * @param {string} type - Notification type (e.g., 'dor_submitted', 'eowr_submitted')
 * @param {string} senderName - Name of the user/system creating the notification
 * @param {object} additionalData - Any additional data to store with the notification
 * @returns {Promise<string>} - The ID of the created notification
 */
export const createNotification = async (
  userId,
  message,
  title = null,
  type = 'general',
  senderName = null,
  additionalData = {}
) => {
  try {
    const notificationData = {
      userId,
      message,
      title,
      type,
      read: false,
      timestamp: serverTimestamp(),
      senderName,
      ...additionalData
    };

    const docRef = await addDoc(collection(db, 'notifications'), notificationData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating notification:', error);
    throw error;
  }
};

/**
 * Creates notifications for report submission
 * @param {string} traineeId - The trainee's user ID
 * @param {string} traineeName - The trainee's name
 * @param {string} trainerName - The trainer's name
 * @param {string} reportType - The type of report ('DOR', 'EOWR', 'OSR')
 * @param {string} customReportId - The custom human-readable ID of the report (e.g., "25-05-DOR-00003")
 * @param {string} firestoreDocId - The Firestore Document ID of the created report (for linking)
 * @param {Date} reportDate - The date of the report
 * @returns {Promise<void>}
 */
export const createReportNotification = async (
  traineeId,
  traineeName,
  trainerName,
  reportType, // This is the short type like 'DOR', 'EOWR', 'OSR'
  customReportId, 
  firestoreDocId, 
  reportDate
) => {
  const formattedDate = reportDate instanceof Date 
    ? reportDate.toLocaleDateString() 
    : new Date(reportDate).toLocaleDateString();
  
  const typeKey = reportType.toLowerCase();
  const fullReportTypeName = getFullReportName(reportType);
  let linkToPath = '';
  
  // Construct the link based on report type using the Firestore Document ID
  switch (reportType) {
    case 'DOR':
      linkToPath = `/dor/${firestoreDocId}`;
      break;
    case 'EOWR':
      linkToPath = `/endofweekreport/${firestoreDocId}`;
      break;
    case 'OSR':
      linkToPath = `/officersafetyreport/${firestoreDocId}`;
      break;
    default:
      linkToPath = '/'; 
  }
  
  // Update notification message and title
  const message = `${trainerName} has submitted a new ${fullReportTypeName}(${formattedDate}) for you.`;
  const title = `New ${fullReportTypeName} Submitted`;
  
  await createNotification(
    traineeId,
    message,
    title,
    `${typeKey}_submitted`,
    null, // trainerName, // Sender name is part of the message
    // Removed customReportId from here as it's no longer in the message/title
    // Retain reportId (firestoreDocId) for potential internal use if needed, but linkTo is primary for navigation
    { reportId: firestoreDocId, reportDate, linkTo: linkToPath } 
  );
};

/**
 * Creates a notification for a report approval or feedback
 * @param {string} userId - The user to notify (usually the trainee)
 * @param {string} userName - The user's name
 * @param {string} senderName - The person providing feedback (usually the trainer or supervisor)
 * @param {string} reportType - The type of report ('DOR', 'EOWR', 'OSR')
 * @param {string} firestoreDocId - The Firestore Document ID of the report
 * @param {string} customReportId - Optional: if you want to display the custom ID in the message
 * @param {string} status - The approval status (e.g., 'approved', 'rejected', 'feedback')
 * @param {string} message - Optional custom message
 * @returns {Promise<string>} - The ID of the created notification
 */
export const createReportApprovalNotification = async (
  userId,
  userName,
  senderName,
  reportType, // This is the short type like 'DOR', 'EOWR', 'OSR'
  firestoreDocId, 
  customReportId, // Kept for potential internal use, but not used in default message/title
  status,
  message = null
) => {
  const fullReportTypeName = getFullReportName(reportType);

  // Updated messages and titles
  const defaultMessage = status === 'approved' 
    ? `Your ${fullReportTypeName} has been approved by ${senderName}.`
    : status === 'rejected'
    ? `Your ${fullReportTypeName} requires revisions. Please check comments from ${senderName}.`
    : `${senderName} has provided feedback on your ${fullReportTypeName}.`;

  const title = status === 'approved' 
    ? `${fullReportTypeName} Approved` 
    : status === 'rejected'
    ? `${fullReportTypeName} Needs Revision`
    : `${fullReportTypeName} Feedback`;

  let linkToPath = '';
  switch (reportType) {
    case 'DOR':
      linkToPath = `/dor/${firestoreDocId}`;
      break;
    case 'EOWR':
      linkToPath = `/endofweekreport/${firestoreDocId}`;
      break;
    case 'OSR':
      linkToPath = `/officersafetyreport/${firestoreDocId}`;
      break;
    default:
      linkToPath = '/';
  }

  return createNotification(
    userId,
    message || defaultMessage,
    title,
    `${reportType.toLowerCase()}_${status}`,
    null, // senderName, // Sender name is part of the message
    // Removed customReportId from here, reportId (firestoreDocId) is for internal use
    { reportId: firestoreDocId, status, linkTo: linkToPath }
  );
};

/**
 * Creates reminder notifications for incomplete tasks
 * @param {string} userId - The user to remind
 * @param {string} userName - The user's name
 * @param {string} reminderType - Type of reminder (e.g., 'dor_needed', 'eowr_needed')
 * @param {string} message - The reminder message
 * @returns {Promise<string>} - The ID of the created notification
 */
export const createReminderNotification = async (
  userId,
  userName,
  reminderType,
  message
) => {
  const title = reminderType === 'dor_needed' 
    ? 'DOR Reminder' 
    : reminderType === 'eowr_needed'
    ? 'EOWR Reminder'
    : 'Action Needed';

  return createNotification(
    userId,
    message,
    title,
    reminderType,
    'System',
    { priority: 'medium' }
  );
};

/**
 * Broadcasts a changelog notification to all active users or users with specific roles
 * @param {string} message - The changelog message content
 * @param {string} senderName - Name of sender (usually an admin)
 * @param {string|Array<string>} [targetRoles] - Optional role(s) to target, if not provided, sends to all
 * @returns {Promise<number>} - Number of notifications sent
 */
export const broadcastChangeLog = async (
  message,
  senderName,
  targetRoles = null
) => {
  try {
    // Get today's date in the desired format (MM/DD/YYYY)
    const today = new Date();
    const formattedDate = today.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
    
    // Create the title with today's date
    const title = `Change Log [${formattedDate}]`;
    
    // Create the query to find users to notify
    let usersQuery;
    
    if (targetRoles) {
      // Convert to array if string
      const roles = Array.isArray(targetRoles) ? targetRoles : [targetRoles];
      usersQuery = query(
        collection(db, 'users'),
        where('active', '==', true),
        where('role', 'in', roles)
      );
    } else {
      // Target all active users
      usersQuery = query(
        collection(db, 'users'),
        where('active', '==', true)
      );
    }
    
    // Get the users
    const usersSnapshot = await getDocs(usersQuery);
    
    if (usersSnapshot.empty) {
      return 0;
    }
    
    // Prepare batch write
    const batch = writeBatch(db);
    const notificationsRef = collection(db, 'notifications');
    let count = 0;
    
    // Add a notification for each user
    usersSnapshot.forEach(userDoc => {
      batch.set(doc(notificationsRef), {
        userId: userDoc.id,
        message,
        title,
        type: 'changelog', // Special type for changelogs
        read: false,
        timestamp: serverTimestamp(),
        senderName,
        priority: 'high',
        senderRole: 'admin'
      });
      
      count++;
    });
    
    // Commit the batch
    await batch.commit();
    return count;
  } catch (error) {
    console.error('Error broadcasting changelog:', error);
    throw error;
  }
};

/**
 * Broadcasts a notification to all active users or users with specific roles
 * @param {string} message - The message to broadcast
 * @param {string} title - The notification title
 * @param {string} senderName - Name of sender (usually an admin)
 * @param {string|Array<string>} [targetRoles] - Optional role(s) to target, if not provided, sends to all
 * @returns {Promise<number>} - Number of notifications sent
 */
export const broadcastNotification = async (
  message,
  title,
  senderName,
  targetRoles = null
) => {
  try {
    // Create the query to find users to notify
    let usersQuery;
    
    if (targetRoles) {
      // Convert to array if string
      const roles = Array.isArray(targetRoles) ? targetRoles : [targetRoles];
      usersQuery = query(
        collection(db, 'users'),
        where('active', '==', true),
        where('role', 'in', roles)
      );
    } else {
      // Target all active users
      usersQuery = query(
        collection(db, 'users'),
        where('active', '==', true)
      );
    }
    
    // Get the users
    const usersSnapshot = await getDocs(usersQuery);
    
    if (usersSnapshot.empty) {
      return 0;
    }
    
    // Prepare batch write
    const batch = writeBatch(db);
    const notificationsRef = collection(db, 'notifications');
    let count = 0;
    
    // Add a notification for each user
    usersSnapshot.forEach(userDoc => {
      batch.set(doc(notificationsRef), {
        userId: userDoc.id,
        message,
        title,
        type: 'broadcast',
        read: false,
        timestamp: serverTimestamp(),
        senderName,
        priority: 'high',
        senderRole: 'admin'
      });
      
      count++;
    });
    
    // Commit the batch
    await batch.commit();
    return count;
  } catch (error) {
    console.error('Error broadcasting notification:', error);
    throw error;
  }
};

/**
 * Creates a notification for when a user (typically a trainee) comments on a report
 * @param {string} reportId - The Firestore document ID of the report 
 * @param {string} reportType - The type of report ('DOR', 'EOWR', 'OSR')
 * @param {string} commentingUserId - User ID of the person leaving the comment
 * @param {string} commentingUserName - Name of the person leaving the comment
 * @param {string} commentingUserRole - Role of the commenting user ('trainee', 'trainer', 'admin')
 * @param {string} commentText - The text of the comment
 * @returns {Promise<number>} - The number of notifications sent
 */
export const createCommentNotification = async (
  reportId,
  reportType,
  commentingUserId,
  commentingUserName,
  commentingUserRole,
  commentText
) => {
  try {
    const fullReportTypeName = getFullReportName(reportType);
    let collectionName;
    switch (reportType) {
      case 'DOR': collectionName = 'dors'; break;
      case 'EOWR': collectionName = 'endOfWeekReports'; break;
      case 'OSR': collectionName = 'officerSafetyReports'; break;
      default: throw new Error(`Unknown report type: ${reportType}`);
    }

    const reportRef = doc(db, collectionName, reportId);
    const reportSnap = await getDoc(reportRef);
    if (!reportSnap.exists()) throw new Error(`Report not found: ${reportId}`);
    const reportData = reportSnap.data();

    const timestamp = serverTimestamp();
    const notificationsRef = collection(db, 'notifications');
    
    const commentPreview = commentText.length > 60 ? `${commentText.substring(0, 60)}...` : commentText;
    let linkToPath = '';
    switch (reportType) {
      case 'DOR': linkToPath = `/dor/${reportId}`; break;
      case 'EOWR': linkToPath = `/endofweekreport/${reportId}`; break;
      case 'OSR': linkToPath = `/officersafetyreport/${reportId}`; break;
      default: linkToPath = '/';
    }

    const notificationDataType = `${reportType.toLowerCase()}_comment`;
    const notificationsToSend = new Map(); // Use a Map to ensure each user gets one tailored notification

    // Prepare notification for the Trainee (subject of the report)
    if (reportData.traineeId && reportData.traineeId !== commentingUserId) {
      const messageForTrainee = `${commentingUserName} commented on your ${fullReportTypeName}: "${commentPreview}"`;
      const titleForTrainee = `New Comment on your ${fullReportTypeName}`;
      notificationsToSend.set(reportData.traineeId, { 
        message: messageForTrainee, 
        title: titleForTrainee,
        recipientContext: 'trainee'
      });
    }

    // Prepare notification for the Report Creator
    if (reportData.createdBy && reportData.createdBy !== commentingUserId) {
      const traineeDisplayName = reportData.traineeName || (reportData.traineeIdNumber ? `Trainee #${reportData.traineeIdNumber}` : 'the trainee');
      const messageForCreator = `${commentingUserName} commented on the ${fullReportTypeName} you created for ${traineeDisplayName}: "${commentPreview}"`;
      const titleForCreator = `New Comment on Report for ${traineeDisplayName}`;
      // Avoid overwriting trainee notification if creator is also the trainee (edge case, but good to handle)
      if (!notificationsToSend.has(reportData.createdBy) || notificationsToSend.get(reportData.createdBy).recipientContext !== 'trainee') {
        notificationsToSend.set(reportData.createdBy, { 
          message: messageForCreator, 
          title: titleForCreator,
          recipientContext: 'creator'
        });
      }
    }

    // Prepare notifications for all other unique previous commenters
    if (reportData.comments && Array.isArray(reportData.comments)) {
      const traineeDisplayName = reportData.traineeName || (reportData.traineeIdNumber ? `Trainee #${reportData.traineeIdNumber}` : 'the trainee');
      const uniqueCommenterIds = new Set(reportData.comments.map(comment => comment.userId));
      
      uniqueCommenterIds.forEach(userId => {
        // Don't notify the person who just made the comment
        // Also, don't send another notification if they are already the trainee or creator and have a tailored message
        if (userId !== commentingUserId && !notificationsToSend.has(userId)) {
          const messageForPreviousCommenter = `${commentingUserName} also commented on the ${fullReportTypeName} for ${traineeDisplayName} (Report ID: ${reportData.reportId || reportId}): "${commentPreview}"`;
          const titleForPreviousCommenter = `New Comment on Report for ${traineeDisplayName}`;
          notificationsToSend.set(userId, {
            message: messageForPreviousCommenter,
            title: titleForPreviousCommenter,
            recipientContext: 'previous_commenter'
          });
        }
      });
    }

    if (notificationsToSend.size === 0) {
      console.log('No users to notify for this comment.');
      return 0;
    }

    const batch = writeBatch(db);
    let count = 0;

    notificationsToSend.forEach(({ message, title }, userIdToNotify) => {
      const newNotifRef = doc(notificationsRef); // Create a new doc reference for each notification
      const notificationPayload = {
        userId: userIdToNotify,
        message,
        title,
        type: notificationDataType,
        read: false,
        timestamp,
        senderName: null, // commentingUserName, // Sender name is part of the message
        reportId,
        commentBy: commentingUserId,
        commentRole: commentingUserRole, 
        linkTo: linkToPath,
        commentTimestamp: timestamp,
        priority: 'high',
        isComment: true,
        reportType: reportType.toLowerCase(),
      };
      batch.set(newNotifRef, notificationPayload);
      console.log(`Preparing notification for ${userIdToNotify}: `, notificationPayload);
      count++;
    });
    
    await batch.commit();
    console.log(`Successfully sent ${count} comment notifications via batch.`);
    return count;

  } catch (error) {
    console.error('Error in createCommentNotification (outer):', error);
    throw error; 
  }
}; 